import fabric from '@/lib/fabric';
import { clientToArtboard } from '@/lib/guideLines';

export type TextDragTool = 'text' | 'textbox';

export interface TextDragCreateOptions {
  canvas: fabric.Canvas;
  getTool: () => string;
  getStyle: () => { fill: string; opacity: number };
  onCommitted: () => void;
  onSwitchToSelect: () => void;
}

type TextLike = fabric.IText | fabric.Textbox;

function finalizeTextObject(obj: TextLike): void {
  if (typeof (obj as fabric.Textbox & { initDimensions?: () => void }).initDimensions === 'function') {
    (obj as fabric.Textbox & { initDimensions: () => void }).initDimensions();
  }
  obj.setCoords();
}

/**
 * Same creation model as Libraries → Text (insertElement):
 * - Heading-style → IText with a fontSize
 * - Body-style → Textbox with width + fontSize
 * No residual scaleX/scaleY — resize baking then matches Libraries behavior.
 */
function createLibraryStyleText(
  tool: TextDragTool,
  opts: {
    left: number;
    top: number;
    dragW: number;
    dragH: number;
    clicked: boolean;
    fill: string;
    opacity: number;
  }
): TextLike {
  const { left, top, dragW, dragH, clicked, fill, opacity } = opts;

  if (tool === 'text') {
    // Point / heading text — font size from drag height (Libraries heading ≈ 48)
    const fontSize = clicked
      ? 48
      : Math.max(12, Math.min(200, Math.round(dragH * 0.75)));
    return new fabric.IText('Enter text', {
      left,
      top,
      fontSize,
      fontWeight: 700,
      fontFamily: 'Outfit, Arial, sans-serif',
      fill,
      opacity,
      originX: 'left',
      originY: 'top',
      editable: true,
      selectable: true,
      evented: true,
      hasControls: true,
      hasBorders: true,
    });
  }

  // Area / body text — width from drag, font size from height (Libraries body ≈ 18)
  const width = clicked ? 420 : Math.max(80, dragW);
  const fontSize = clicked
    ? 18
    : Math.max(12, Math.min(72, Math.round(dragH * 0.28)));
  return new fabric.Textbox('Enter text', {
    left,
    top,
    width,
    fontSize,
    fontWeight: 400,
    fontFamily: 'Source Sans 3, Arial, sans-serif',
    fill,
    opacity,
    originX: 'left',
    originY: 'top',
    editable: true,
    selectable: true,
    evented: true,
    hasControls: true,
    hasBorders: true,
  });
}

/**
 * Pointer-capture drag-to-create for Type tools — mirrors Libraries text objects.
 */
export function installTextDragCreate(options: TextDragCreateOptions): () => void {
  const { canvas, getTool, getStyle, onCommitted, onSwitchToSelect } = options;

  const upper =
    (canvas as fabric.Canvas & { upperCanvasEl?: HTMLCanvasElement }).upperCanvasEl ||
    canvas.getElement();

  type Session = {
    tool: TextDragTool;
    originX: number;
    originY: number;
    preview: fabric.Rect;
    pointerId: number;
  };

  let session: Session | null = null;

  const clearPreview = () => {
    if (!session) return;
    canvas.remove(session.preview);
    session = null;
    canvas.requestRenderAll();
  };

  const updatePreview = (clientX: number, clientY: number) => {
    if (!session) return;
    const p = clientToArtboard(canvas, clientX, clientY);
    const left = Math.min(session.originX, p.x);
    const top = Math.min(session.originY, p.y);
    const width = Math.max(1, Math.abs(p.x - session.originX));
    const height = Math.max(1, Math.abs(p.y - session.originY));
    session.preview.set({ left, top, width, height });
    session.preview.setCoords();
    canvas.requestRenderAll();
  };

  const commit = (clientX: number, clientY: number) => {
    if (!session) return;
    const { tool, originX, originY, preview } = session;

    const boxLeft = preview.left ?? originX;
    const boxTop = preview.top ?? originY;
    const boxW = Math.max(1, (preview.width || 1) * (preview.scaleX || 1));
    const boxH = Math.max(1, (preview.height || 1) * (preview.scaleY || 1));
    const clicked = boxW < 8 && boxH < 8;

    canvas.remove(preview);
    session = null;

    const { fill, opacity } = getStyle();
    const obj = createLibraryStyleText(tool, {
      left: clicked ? originX : boxLeft,
      top: clicked ? originY : boxTop,
      dragW: boxW,
      dragH: boxH,
      clicked,
      fill,
      opacity,
    });

    finalizeTextObject(obj);
    canvas.add(obj);
    canvas.setActiveObject(obj);
    canvas.requestRenderAll();

    // Do NOT auto-enter editing (Libraries don't) — that was leaving Type-tool
    // text in a bad scale/fontSize state vs Libraries text.
    onSwitchToSelect();
    onCommitted();
  };

  const onPointerDown = (e: PointerEvent) => {
    if (e.button !== 0) return;
    const tool = getTool();
    if (tool !== 'text' && tool !== 'textbox') return;
    if (session) return;

    const prevSkip = canvas.skipTargetFind;
    canvas.skipTargetFind = false;
    let target: fabric.Object | undefined | null = null;
    try {
      target = canvas.findTarget?.(e as unknown as Event, false) || null;
    } finally {
      canvas.skipTargetFind = prevSkip;
    }
    if (target && !target.excludeFromExport) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const p = clientToArtboard(canvas, e.clientX, e.clientY);
    const preview = new fabric.Rect({
      left: p.x,
      top: p.y,
      width: 1,
      height: 1,
      fill: 'rgba(13, 115, 119, 0.12)',
      stroke: '#0d7377',
      strokeWidth: 1.5,
      strokeDashArray: [6, 4],
      selectable: false,
      evented: false,
      excludeFromExport: true,
      objectCaching: false,
      originX: 'left',
      originY: 'top',
    });
    canvas.discardActiveObject();
    canvas.selection = false;
    canvas.add(preview);
    canvas.requestRenderAll();

    session = {
      tool: tool as TextDragTool,
      originX: p.x,
      originY: p.y,
      preview,
      pointerId: e.pointerId,
    };

    try {
      upper.setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  };

  const onPointerMove = (e: PointerEvent) => {
    if (!session || e.pointerId !== session.pointerId) return;
    e.preventDefault();
    updatePreview(e.clientX, e.clientY);
  };

  const onPointerUp = (e: PointerEvent) => {
    if (!session || e.pointerId !== session.pointerId) return;
    e.preventDefault();
    try {
      upper.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    commit(e.clientX, e.clientY);
  };

  const onPointerCancel = (e: PointerEvent) => {
    if (!session || e.pointerId !== session.pointerId) return;
    try {
      upper.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    clearPreview();
  };

  upper.addEventListener('pointerdown', onPointerDown, { capture: true });
  upper.addEventListener('pointermove', onPointerMove, { capture: true });
  upper.addEventListener('pointerup', onPointerUp, { capture: true });
  upper.addEventListener('pointercancel', onPointerCancel, { capture: true });

  return () => {
    clearPreview();
    upper.removeEventListener('pointerdown', onPointerDown, true);
    upper.removeEventListener('pointermove', onPointerMove, true);
    upper.removeEventListener('pointerup', onPointerUp, true);
    upper.removeEventListener('pointercancel', onPointerCancel, true);
  };
}

/**
 * After handle-resize: turn scale into real fontSize/width (Libraries-compatible).
 * scale always ends at 1 so click-to-edit matches the outer box.
 */
export function bakeTextObjectScale(obj: fabric.Object): boolean {
  const type = obj.type;
  if (type !== 'i-text' && type !== 'text' && type !== 'textbox') return false;

  const sx = obj.scaleX || 1;
  const sy = obj.scaleY || 1;
  if (Math.abs(sx - 1) < 0.001 && Math.abs(sy - 1) < 0.001) return false;

  const left = obj.left ?? 0;
  const top = obj.top ?? 0;

  if (type === 'textbox') {
    const box = obj as fabric.Textbox;
    box.set({
      left,
      top,
      width: Math.max(20, (box.width || 100) * Math.abs(sx)),
      fontSize: Math.max(8, Math.round((box.fontSize || 18) * Math.abs(sy))),
      scaleX: 1,
      scaleY: 1,
    });
    finalizeTextObject(box);
  } else {
    const text = obj as fabric.IText;
    const scale = (Math.abs(sx) + Math.abs(sy)) / 2;
    text.set({
      left,
      top,
      fontSize: Math.max(8, Math.round((text.fontSize || 28) * scale)),
      scaleX: 1,
      scaleY: 1,
    });
    finalizeTextObject(text);
  }

  return true;
}
